import { Injectable } from '@angular/core';
import { BuildInfoModel, LIST_SEARCH_MODE, ListSearchMode, PowerLineConfig, ToolbarMenu, UserModel } from '../model/general.model';
import { saveAs } from 'file-saver';
import { NgxSpinnerService } from 'ngx-spinner';
import { ProjectsService } from './projects.service';
import { Project } from '../model/project.model';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import {
  DATA_MENU_ITEM,
  DEFECTS_MENU_ITEM,
  LOCAL_STORAGE_KEYS,
  MAP_MENU_ITEM,
  ORDERS_MENU_ITEM,
  REPORTS_MENU_ITEM,
  SETTINGS_MENU_ITEM,
  TASKS_MENU_ITEM
} from '../config/general.config';
import { Router } from '@angular/router';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  finalize,
  firstValueFrom,
  forkJoin,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  tap
} from 'rxjs';
import { PermissionRole } from '../config/permission.config';
import { AttachmentService } from './attachment.service';
import { HttpEvent, HttpEventType, HttpProgressEvent, HttpResponse } from '@angular/common/http';
import { GO_TASK_URL, ORDER_DETAILS_URL, ORDERS_URL, UNAUTHORIZED_URL } from '../config/url.config';
import { Location } from '@angular/common';
import { GeneralService } from './general.service';
import { ConnectionStatusService } from './connection-status.service';
import { OrdersService } from './orders.service';
import { GridState, SettingsKey, UserSettings } from '../model/settings.model';
import { getFileNameFromHeaders } from '../utils/general.utils';

@Injectable({
  providedIn: 'root'
})
export class StoreService {

  public userSettings: UserSettings[] = [];
  public tasksGridSelectedFilter: string;
  public tasksGridFilters: GridState[] = [];
  public gridRestoreSetup = new Subject<GridState>();

  private _permissions: string[] = [];
  private _loggedUser: UserModel;
  private _beBuildInfo: BuildInfoModel;

  get loggedUser(): UserModel {
    return this._loggedUser;
  }

  get beBuildInfo(): BuildInfoModel {
    return this._beBuildInfo;
  }

  get permissions(): string[] {
    return this._permissions;
  }

  setPermissions(permission: string[]) {
    this._permissions = permission;
  }

  hasAccess(): boolean {
    return this.permissions.includes(PermissionRole.ACCESS);
  }

  isAdmin(): boolean {
    return this.permissions.includes(PermissionRole.ADMIN);
  }

  canSeeOrders(): boolean {
    return this.challengeProjectPermission(PermissionRole.TASKS_RELEASE) || this.isAdmin();
  }

  canCloseOrders(): boolean {
    return this.challengeProjectPermission(PermissionRole.TASKS_RELEASE) || this.isAdmin();
  }

  canSeeDefects(): boolean {
    return this.hasTaskEditRole() || this.hasTaskApproveRole() || this.isAdmin();
  }

  canSeeData(): boolean {
    return this.challengeProjectPermission(PermissionRole.DATA_VIEW) || this.isAdmin();
  }

  canMultiApprove(): boolean {
    return this.challengeProjectPermission(PermissionRole.MULTI_APPROVE);
  }

  canSeeReports(): boolean {
    return this.challengeProjectPermission(PermissionRole.TASKS_RELEASE)
      || this.hasTaskEditRole()
      || this.hasTaskApproveRole()
      || this.isAdmin();
  }

  hasTaskEditRole(): boolean {
    return this.challengeProjectPermission(PermissionRole.TASKS_EDIT)
  }

  hasTaskReceiveRole(): boolean {
    return this.challengeProjectPermission(PermissionRole.TASKS_RECEIVE)
  }

  hasTaskApproveRole(): boolean {
    return this.challengeProjectPermission(PermissionRole.TASKS_APPROVE)
  }

  hasTaskReleaseRole(): boolean {
    return this.challengeProjectPermission(PermissionRole.TASKS_RELEASE)
  }

  hasTaskReceiveProRole(): boolean {
    return this.challengeProjectPermission(PermissionRole.TASKS_RECEIVE_PRO);
  }

  canSeeTasks(): boolean {
    return this.challengeProjectPermission(PermissionRole.TASKS_RECEIVE)
      || this.hasTaskEditRole()
      || this.hasTaskApproveRole()
      || this.challengeProjectPermission(PermissionRole.TASKS_VIEW_ALL)
      || this.isAdmin();
  }

  canSeeMap(): boolean {
    return this.challengeProjectPermission(PermissionRole.TASKS_RECEIVE)
      || this.challengeProjectPermission(PermissionRole.TASKS_VIEW_ALL)
      || this.challengeProjectPermission(PermissionRole.TASKS_EDIT)
      || this.challengeProjectPermission(PermissionRole.TASKS_RELEASE)
      || this.challengeProjectPermission(PermissionRole.TASKS_APPROVE)
      || this.isAdmin();
  }

  isAuthor(userId: string): boolean {
    return userId === this._loggedUser.id;
  }

  isUserAssignedToTask(assignees: UserModel[]): boolean {
    return assignees.map(user => user.id).includes(this._loggedUser.id);
  }

  private _appReady$ = new BehaviorSubject<boolean>(false);
  appReadyChanged$ = this._appReady$.asObservable();
  loaderText = '';
  projects: Project[] = [];

  private readonly _projectChanged$ = new BehaviorSubject<Project | undefined>(undefined);
  projectChanged$ = this._projectChanged$.asObservable();

  toolbarMenu: ToolbarMenu[] = [];
  private menuRefreshTrigger$ = new BehaviorSubject<undefined>(undefined)

  private _selectedProject: Project;

  private projectSelectorVisible = new BehaviorSubject<boolean>(true);
  projectSelectVisible$ = this.projectSelectorVisible.asObservable();

  showProjectSelector() {
    this.projectSelectorVisible.next(true);
  }

  hideProjectSelector() {
    setTimeout(()=> {
      this.projectSelectorVisible.next(false);
    },0)
  }

  setSelectedProject(project: Project, emitEvent = true) {
    this._selectedProject = project;
    localStorage.setItem(LOCAL_STORAGE_KEYS.engoSelectedProject, JSON.stringify(project));

    if (emitEvent) {
      this._projectChanged$.next(project);
    }
    this.prepareMenuItems();
  }

  get selectedProject(): Project {
    return this._selectedProject;
  }

  private _orders$ = new BehaviorSubject<void>(undefined);
  private ordersListMode$ = new BehaviorSubject<ListSearchMode>(LIST_SEARCH_MODE.EXTERNAL);
  orders$ = combineLatest([
    this._orders$,
    this.projectChanged$,
    this.ordersListMode$
  ]).pipe(
    tap(([_, project]) => {
      if (!project || !this.canSeeOrders()) {
        void this._router.navigate(['']);
      }
    }),
    filter(([_, project]) => !!project && this.canSeeOrders()),
    switchMap(([_, project, mode]) => {
      this.showLoader();
      switch (mode) {
        case LIST_SEARCH_MODE.EXTERNAL:
          return this._projectService.getExternalOrders(project!.id);
        case LIST_SEARCH_MODE.ACTIVE:
          return this._projectService.getActiveOrders(project!.id);
        case LIST_SEARCH_MODE.ARCHIVED:
          return this._projectService.getArchivedOrders(project!.id);
        case LIST_SEARCH_MODE.ALL:
          return this._projectService.getAllOrders(project!.id);
        default:
          return of([]);
      }
    }),
    shareReplay({refCount: true, bufferSize: 1}),
    tap(() => this.hideLoader())
  );


  private powerLineConfig$ = new Subject<PowerLineConfig>();

  setPowerLineConfig(config: PowerLineConfig) {
    this.powerLineConfig$.next(config)
  }

  getPowerLineConfig() {
    return this.powerLineConfig$.asObservable();
  }

  powerLineHidden = false;

  constructor(
    private _loader: NgxSpinnerService,
    private _projectService: ProjectsService,
    private _attachmentService: AttachmentService,
    private _toastr: ToastrService,
    private _translate: TranslateService,
    private _generalService: GeneralService,
    private _router: Router,
    private _location: Location,
    private connectionStatusService: ConnectionStatusService,
    private ordersService: OrdersService
  ) {
  }

  initApp(): Promise<any> {
    if (!this.hasAccess()) {
      this._appReady$.next(true);
      return Promise.resolve();
    }

    return firstValueFrom(
      this.connectionStatusService.networkStatus$.pipe(
        switchMap(isOnline => {
          if(isOnline) {
            return forkJoin({
              projects: this._projectService.getProjects(),
              loggedUser: this._generalService.getLoggedUser(),
              settings: this._generalService.getUserSettings(),
              beBuildInfo: this._generalService.getBuildInfo()
            })
          } else {
            return forkJoin({
              projects: of((JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.projects) || '[]')) as Project[]),
              loggedUser: of(JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.user) || '') as UserModel),
              settings: of([]),
              beBuildInfo: of(JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.buildInfo) || 'null') as BuildInfoModel)
            })
          }
        })
      )
      .pipe(
        tap(({ projects, loggedUser, settings, beBuildInfo }) => {
          this.projects = projects;
          this._loggedUser = Object.seal(loggedUser);
          this._beBuildInfo = beBuildInfo;
          this.assignUserSettings(settings)

          this._setSelectedProjectFromStorage();

          // special handler when user logout before with no access and then login with another account with access
          if (this._location.path() === `/${UNAUTHORIZED_URL}` && this.hasAccess()) {
            void this._router.navigate(['/']);
          }
          this.prepareMenuItems();
          this._appReady$.next(true);
        }),
        tap(({ projects, loggedUser, beBuildInfo }) => this._storeDataForOfflineUse({projects, loggedUser, beBuildInfo}))
      )
    );
  }

  private _setSelectedProjectFromStorage(): void {
    const storedProjectJson = localStorage.getItem(LOCAL_STORAGE_KEYS.engoSelectedProject);

    if (!storedProjectJson) {
      this.specialHandlerForTasksAndOrders();
      return;
    }

    const { id: storedProjectId } = JSON.parse(storedProjectJson);
    const project = this.projects.find(item => item.id === storedProjectId);

    if (project) {
      this.setSelectedProject(project);
    }
  }

  private specialHandlerForTasksAndOrders(): void {
    const path = this._location.path().slice(1);
    const idRegex = /\/(\d+)$/;

    const processItem = (regex: RegExp, serviceMethod: (id: number) => Observable<any>) => {
      if (regex.test(path)) {
        void this._loader.show();
        const match = path.match(idRegex);
        const id = match ? match[1] : null;
        if (id) {
          serviceMethod(parseInt(id)).pipe(
            tap(item => {
              const project = this.projects.find(x => x.id === item.project.id);
              if (project) {
                this.setSelectedProject(project);
              }
            })
          ).subscribe();
        }
      }
    };

    const taskRegex = new RegExp(`^${GO_TASK_URL}\/\\d+`);
    const orderRegex = new RegExp(`^${ORDERS_URL}\/\\d+|^${ORDER_DETAILS_URL}\/\\d+`);

    processItem(taskRegex, this._generalService.getTask.bind(this._generalService));
    processItem(orderRegex, this.ordersService.getOrderById.bind(this.ordersService));
  }

  private _storeDataForOfflineUse(data: {projects: Project[], loggedUser: UserModel, beBuildInfo: BuildInfoModel}): void {
    localStorage.setItem(LOCAL_STORAGE_KEYS.loggedIn, JSON.stringify(true));
    localStorage.setItem(LOCAL_STORAGE_KEYS.user, JSON.stringify(data.loggedUser));
    localStorage.setItem(LOCAL_STORAGE_KEYS.projects, JSON.stringify(data.projects));
    localStorage.setItem(LOCAL_STORAGE_KEYS.buildInfo, JSON.stringify(data.beBuildInfo));
  }

  refreshProject(projectId?: string): void {
    this._projectService.getProject(projectId || this.selectedProject.id).pipe(
      tap(project => this.projects = this.projects.map(proj => proj.id === project.id ? project : proj)),
      tap(project => this.setSelectedProject(project, false))
    ).subscribe();
  }

  refreshOrders(): void {
    this._orders$.next();
  }

  setOrdersListMode(mode: ListSearchMode): void {
    this.ordersListMode$.next(mode);
  }

  getOrdersListMode() {
    return this.ordersListMode$;
  }

  prepareMenuItems(): void {
    merge([
      this.menuRefreshTrigger$,
      this.connectionStatusService.networkStatus$,
      this._projectChanged$,
    ])
    .pipe(
      switchMap(() => this.connectionStatusService.networkStatus$),
      tap(() => this.toolbarMenu = []),
      tap(isOnline => {
        if (this.canSeeTasks()) {
          const taskItem = TASKS_MENU_ITEM;
          taskItem.extraLabel = this.selectedProject?.actionsCount ?? '';
          this.toolbarMenu.push(taskItem);
        }
        if(isOnline) {
          if (this.canSeeMap()) {
            this.toolbarMenu.push(MAP_MENU_ITEM);
          }

          if (this.canSeeDefects()) {
            this.toolbarMenu.push(DEFECTS_MENU_ITEM);
          }

          if (this.canSeeReports()) {
            this.toolbarMenu.push(REPORTS_MENU_ITEM);
          }

          if (this.canSeeOrders()) {
            this.toolbarMenu.push(ORDERS_MENU_ITEM);
          }

          if (this.canSeeData()) {
            this.toolbarMenu.push(DATA_MENU_ITEM);
          }

          if (this.isAdmin()) {
            this.toolbarMenu.push(SETTINGS_MENU_ITEM);
          }
        }
      })
    ).subscribe();

  }

  refreshMenuItems() {
    this.menuRefreshTrigger$.next(undefined);
  }
  challengeProjectPermission(role: PermissionRole): boolean {
    return !!this.selectedProject?.permissions?.includes(role);
  }

  setUploadProgress(response: HttpProgressEvent): void {
    if (response.type === HttpEventType.UploadProgress && response.total) {
      const progress = Math.round((100 * response.loaded) / response.total);
      this.loaderText = this._translate.instant('PHOTO_UPLOAD.UPLOADING_PROGRESS', { progress });

      if (progress === 100) {
        this.loaderText = this._translate.instant('PHOTO_UPLOAD.PROCESSING');
      }
    }
  }

  setLoaderText(translationKey: string): void {
    this.loaderText = this._translate.instant(translationKey);
  }
  downloadFile(id: number, fileName: string): void {
    void this._loader.show();
    this._attachmentService.download(id)
    .pipe(
      finalize(() => void this._loader.hide())
    )
    .subscribe({
      next: response => {
        this.saveFile(fileName, response)
        void this._loader.hide();
      },
      error: (err) => this.showErrorMsg(err.message)
    })
  }

  saveFile(fileName: string, blob: Blob): void {
    saveAs(blob, fileName);
    this.hideLoader();
  }

  showLoader(): void {
    void this._loader.show();
  }

  hideLoader(): void {
    void this._loader.hide();
  }

  showSuccessMsg(key: string | string[], interpolateParams?: Object | undefined): void {
    this._toastr.success(this.translateKey(key, interpolateParams))
  }

  showErrorMsg(key: string | string[], interpolateParams?: Object | undefined): void {
    this._toastr.error(this.translateKey(key, interpolateParams))
  }

  showWarningMsg(key: string | string[], interpolateParams?: Object | undefined): void {
    this._toastr.warning(this.translateKey(key, interpolateParams))
  }

  private translateKey(key: string | string[], interpolateParams?: Object | undefined) {
    return this._translate.instant(key, interpolateParams);
  }

  updateActionsCount() {
    this.connectionStatusService.networkStatus$.pipe(
      take(1),
      filter(isOnline => isOnline),
      switchMap(() => this._projectService.getProject(this.selectedProject.id)),
      tap(data => {
        this._selectedProject.actionsCount = data.actionsCount;
        this.refreshMenuItems();
      })
    ).subscribe();
  }

  updateSelectedProject(id: string): void {
    const project = this.projects.find(x => x.id === id);

    if (project && project.id !== this._selectedProject.id) {
      this.setSelectedProject(project)
    }
  }

  handleTasksGridFilterChange(filter: string, showMsg = false): void {
    this.tasksGridSelectedFilter = filter;
    const state = this.tasksGridFilters.find(item => item.name === filter);
    if (state) {
      state.showMsg = showMsg;
      this.gridRestoreSetup.next(state);
    }
  }

  saveGridSettings(key: SettingsKey, config: GridState): void {
    void this._loader.show();
    const settings = this.userSettings.find(item => item.key === key);
    const state: GridState[] = settings ? JSON.parse(settings.value) : [];

    const index = state.findIndex(item => item.name === config.name);
    if (index > -1) {
      state[index] = config;
    } else {
      state.push(config);
    }

    this._generalService.saveSetting(key, JSON.stringify(state)).subscribe(() => {
      this._toastr.success(this._translate.instant('SETTINGS.SAVE_SUCCESS'));
      this.getUserSettings();
      if (key === SettingsKey.TASKS_GRID) {
        this.tasksGridSelectedFilter = config.name;
      }

      void this._loader.hide();
    });
  }

  private assignUserSettings(settings: UserSettings[]) {
    this.userSettings = settings;
    this.tasksGridFilters = this.getParsedGridState(settings, SettingsKey.TASKS_GRID);
  }

  private getParsedGridState(settings: UserSettings[], key: SettingsKey): GridState[] {
    const setting = settings.find(item => item.key === key);

    if (!setting) {
      return [];
    }
    const data = JSON.parse(setting.value) as GridState[];
    return data.sort((a, b) => a.name.localeCompare(b.name))
  }

  getUserSettings(): void {
    this._generalService.getUserSettings().subscribe(response => this.assignUserSettings(response));
  }

  handleTasksGridFilterDelete(): void {
    this.handleGridFilterDelete(this.tasksGridFilters, this.tasksGridSelectedFilter, SettingsKey.TASKS_GRID, (value) => this.tasksGridSelectedFilter = value);
  }

  handleGridFilterDelete(filters: any[], selectedFilter: string, settingsKey: SettingsKey, setSelectedFilter: (value: string) => void): void {
    void this._loader.show();
    const state = filters.filter(item => item.name !== selectedFilter);
    if (state) {
      this._generalService.saveSetting(settingsKey, JSON.stringify(state)).subscribe(() => {
        setSelectedFilter('');
        this.handleDeleteFilterSuccessResponse();
      });
    }
  }

  private handleDeleteFilterSuccessResponse(): void {
    this._toastr.success(this._translate.instant('SETTINGS.FILTER_DELETE_SUCCESS'));
    this.getUserSettings();
    void this._loader.hide();
  }

  downloadOrderZip(orderId: number): void {
    this.downloadBlobStream(this.ordersService.downloadZip(orderId));
  }

  downloadBlobStream(source$: Observable<HttpEvent<Blob>>): void {
    this.showLoader();
    this.loaderText = this._translate.instant('DOWNLOAD_PREPARING');
    source$.pipe(
      tap(event => {
        if (event.type === HttpEventType.DownloadProgress && event.total) {
          const percentDone = Math.round((event.loaded / (event as HttpProgressEvent).total!) * 100);
          this.loaderText = this._translate.instant('DOWNLOAD_PROGRESS', { progress: `${percentDone} %` });
        } else if (event.type === HttpEventType.DownloadProgress) {
          this.loaderText = this._translate.instant('DOWNLOAD_PROGRESS', { progress: `${Math.round((event as HttpProgressEvent).loaded * 1e-6)} MB` });
        }
      }),
      map(event => event instanceof HttpResponse ? event as HttpResponse<Blob> : null),
      tap(response => {
        if (response !== null) {
          this.saveFile(getFileNameFromHeaders(response), response.body as Blob);
        }
      }),
      finalize(() => this.loaderText = '')
    ).subscribe();
  }
}
